/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Thu Apr 04 2024
 **/

import { useEffect, useState } from "react";

import { useChatRoomListActions } from "@/frameworks/core/chat";
import { ChatInput } from "@/frameworks/core/components";
import { useMiniChatPopupManager } from "@/frameworks/core/hooks";
import { Configure } from "@/frameworks/libraries/utilities";
import { useChatDataStore } from "@/modules/chat/store";

type ChatFileFormat = (
	| ChatServices.Format2.Elements.File
	| ChatServices.Format2.Elements.Image
	| ChatServices.Format2.Elements.Link
) & { fileBoxID: string };

type ChatUpdateInputProps = {
	roomId: string;
	myRoomID?: string;
	spaceID?: string;
	isMyRoom: boolean;
	messageToUpdate?: ChatServices.Format2.Container.Richtext | null;
	variant?: ChatUI.Size;
	onUpdateSubmit?: (
		line: ChatServices.Format2.Message,
		files?: ChatFileFormat[],
		isEmergency?: boolean,
		plainText?: string
	) => void;
	onUpdateCancel?: () => void;
};

export default function ChatUpdateInput(props: ChatUpdateInputProps) {
	const [userListForMention, setUserListForMention] = useState<{ id: string; value: string }[] | null>(null);
	const chatRoomListActions = useChatRoomListActions();
	const { miniChatPopupActions } = useMiniChatPopupManager();

	useEffect(() => {
		if (props.spaceID) {
			const spaceRoom = chatRoomListActions.getSpaceRoom({ spaceID: props.spaceID, roomID: props.roomId });
			const userList = spaceRoom?.users.filter((v) => v.id !== Configure.userID) ?? [];
			setUserListForMention(userList.map((v) => ({ id: v.id, value: v.name })));
		} else {
			const dmRoom = chatRoomListActions.getDMRoom(props.roomId);
			const userList = dmRoom?.users.filter((v) => v.id !== Configure.userID) ?? [];
			setUserListForMention(userList.map((v) => ({ id: v.id, value: v.name })));
		}
	}, []);

	const handleCloseChatWithKeyboard = (ev: KeyboardEvent) => {
		if (ev.key === "Escape") {
			if (!useChatDataStore.getState().room_ui[props.roomId]?.isUpdating) return;

			if (!useChatDataStore.getState().show_fullsize_chat) {
				const currentFocus = miniChatPopupActions.getCurrentFocus();
				if (currentFocus?.id !== props.roomId) return;
			}

			props.onUpdateCancel?.();
		}
	};

	useEffect(() => {
		window.addEventListener("keydown", handleCloseChatWithKeyboard);
		return () => {
			window.removeEventListener("keydown", handleCloseChatWithKeyboard);
		};
	}, []);

	if (!userListForMention || !props.messageToUpdate) return;
	return (
		<ChatInput
			channelID={props.roomId}
			update={true}
			defaultValue={props.messageToUpdate}
			disabled={false}
			onSubmit={props.onUpdateSubmit}
			onCancel={props.onUpdateCancel}
			myRoomID={props.myRoomID}
			spaceID={props.spaceID}
			type={props.spaceID ? "Space" : "DM"}
			isMyRoom={props.isMyRoom}
			atMentions={userListForMention}
			variant={props.variant}
		/>
	);
}
